import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock,
  LogOut,
  RefreshCw,
  Search,
  Trash2,
  Phone,
  Mail,
  MessageCircle,
  Calendar,
  Download,
  Loader2,
} from "lucide-react";
import { buildWhatsAppLink, telLink } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "smp_admin_token";

export default function AdminPage() {
  const [token, setToken] = useState(
    () => localStorage.getItem(TOKEN_KEY) || ""
  );

  if (!token) {
    return <Login onLogin={(t) => setToken(t)} />;
  }

  return (
    <Dashboard
      token={token}
      onLogout={() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      }}
    />
  );
}

function Login({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success("Welcome back, Sneha.");
      onLogin(data.token);
    } catch (err) {
      toast.error("Invalid password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen flex items-center justify-center px-6 py-32"
    >
      <div className="w-full max-w-md glass-strong p-10 md:p-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-full border border-[#D4AF37] flex items-center justify-center">
            <Lock className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.4} />
          </div>
          <div>
            <div className="eyebrow !mb-0">Admin Console</div>
            <div className="font-serif text-2xl text-white font-light">
              S Mahadeva Properties
            </div>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-6">
          <div>
            <label className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-2 block">
              Password
            </label>
            <input
              type="password"
              data-testid="admin-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              className="w-full bg-transparent border-b border-white/15 text-white py-3 px-0 focus:border-[#D4AF37] outline-none placeholder:text-white/30 transition-colors font-light"
              autoFocus
            />
          </div>
          <button
            type="submit"
            data-testid="admin-login-btn"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-medium tracking-[0.2em] uppercase text-xs px-8 py-4 hover:bg-[#B3932E] transition-all duration-300 shadow-[0_0_25px_rgba(212,175,55,0.25)] disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Signing in
              </>
            ) : (
              "Sign In"
            )}
          </button>
        </form>
        <p className="mt-8 text-[11px] tracking-wider text-white/40 text-center">
          Restricted access · Authorized personnel only.
        </p>
      </div>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/leads`, {
        headers: { "X-Admin-Token": token },
      });
      setLeads(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        onLogout();
      } else {
        toast.error("Failed to load leads.");
      }
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const filtered = useMemo(() => {
    if (!query.trim()) return leads;
    const q = query.toLowerCase();
    return leads.filter((l) =>
      [l.name, l.phone, l.email, l.property_interest, l.budget, l.message]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q))
    );
  }, [leads, query]);

  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await axios.delete(`${API}/leads/${id}`, {
        headers: { "X-Admin-Token": token },
      });
      setLeads((ls) => ls.filter((l) => l.id !== id));
      toast.success("Lead deleted.");
    } catch (err) {
      toast.error("Failed to delete.");
    }
  };

  const exportCSV = () => {
    const headers = [
      "Name",
      "Phone",
      "Email",
      "Budget",
      "Interest",
      "Site Visit Date",
      "Message",
      "Source",
      "Created At",
    ];
    const rows = filtered.map((l) => [
      l.name,
      l.phone,
      l.email || "",
      l.budget || "",
      l.property_interest || "",
      l.site_visit_date || "",
      (l.message || "").replace(/\n/g, " "),
      l.source || "",
      l.created_at,
    ]);
    const csv = [headers, ...rows]
      .map((r) =>
        r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `smp-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  const stats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const week = new Date();
    week.setDate(week.getDate() - 7);
    return {
      total: leads.length,
      today: leads.filter((l) => new Date(l.created_at) >= today).length,
      week: leads.filter((l) => new Date(l.created_at) >= week).length,
    };
  }, [leads]);

  return (
    <div data-testid="admin-dashboard" className="min-h-screen pt-28 pb-20 px-6 md:px-10">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="eyebrow mb-3">Admin Console</div>
            <h1 className="font-serif text-4xl md:text-5xl text-white font-light">
              Lead <span className="gold-gradient-text italic">Pipeline</span>
            </h1>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchLeads}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-white/10 text-white/70 hover:border-[#D4AF37] hover:text-[#D4AF37] text-xs tracking-[0.2em] uppercase transition-all"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} strokeWidth={1.4} />
              Refresh
            </button>
            <button
              onClick={exportCSV}
              data-testid="admin-export"
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37]/10 text-xs tracking-[0.2em] uppercase transition-all"
            >
              <Download className="w-4 h-4" strokeWidth={1.4} />
              Export CSV
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-white/10 text-white/70 hover:border-red-400/50 hover:text-red-400 text-xs tracking-[0.2em] uppercase transition-all"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.4} />
              Logout
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-px bg-white/5 border border-white/5 mb-10">
          {[
            { label: "Total Leads", value: stats.total },
            { label: "Last 7 days", value: stats.week },
            { label: "Today", value: stats.today },
          ].map((s) => (
            <div key={s.label} className="bg-[#0a0a0a] px-6 py-7">
              <div className="font-serif text-4xl md:text-5xl text-[#D4AF37] font-light">
                {s.value}
              </div>
              <div className="mt-2 text-[10px] tracking-[0.25em] uppercase text-white/50">
                {s.label}
              </div>
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" strokeWidth={1.6} />
          <input
            data-testid="admin-search"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, phone, email, interest..."
            className="w-full bg-white/[0.02] border border-white/10 pl-12 pr-4 py-3.5 text-white placeholder:text-white/30 focus:border-[#D4AF37] outline-none transition-colors font-light"
          />
        </div>

        {/* Table */}
        <div className="glass overflow-x-auto" data-testid="admin-leads-table">
          {loading ? (
            <div className="py-20 text-center text-white/50">
              <Loader2 className="w-6 h-6 animate-spin inline-block mr-2" />
              Loading leads...
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center text-white/50">
              {leads.length === 0
                ? "No leads yet. Submissions will appear here."
                : "No leads match your search."}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/5 text-[10px] tracking-[0.25em] uppercase text-white/40">
                  <th className="text-left px-5 py-4 font-medium">Contact</th>
                  <th className="text-left px-5 py-4 font-medium">Interest</th>
                  <th className="text-left px-5 py-4 font-medium">Budget</th>
                  <th className="text-left px-5 py-4 font-medium">Site Visit</th>
                  <th className="text-left px-5 py-4 font-medium">Message</th>
                  <th className="text-left px-5 py-4 font-medium">Received</th>
                  <th className="text-right px-5 py-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <tr
                    key={l.id}
                    data-testid={`admin-lead-row-${l.id}`}
                    className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                  >
                    <td className="px-5 py-5 align-top">
                      <div className="font-serif text-lg text-white">{l.name}</div>
                      <div className="mt-1 text-white/60 font-mono text-xs">
                        {l.phone}
                      </div>
                      {l.email && (
                        <div className="text-white/50 text-xs mt-1 break-all">
                          {l.email}
                        </div>
                      )}
                      <div className="mt-2 text-[10px] tracking-[0.2em] uppercase text-white/30">
                        {l.source}
                      </div>
                    </td>
                    <td className="px-5 py-5 align-top text-white/80 font-light">
                      {l.property_interest || "—"}
                    </td>
                    <td className="px-5 py-5 align-top text-[#D4AF37] font-light">
                      {l.budget || "—"}
                    </td>
                    <td className="px-5 py-5 align-top text-white/70 font-light">
                      {l.site_visit_date ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-[#D4AF37]" strokeWidth={1.5} />
                          {l.site_visit_date}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-5 py-5 align-top text-white/60 max-w-xs">
                      <p className="line-clamp-3 font-light">
                        {l.message || "—"}
                      </p>
                    </td>
                    <td className="px-5 py-5 align-top text-white/50 text-xs whitespace-nowrap">
                      {formatDate(l.created_at)}
                    </td>
                    <td className="px-5 py-5 align-top">
                      <div className="flex items-center justify-end gap-2">
                        <a
                          href={telLink(l.phone)}
                          title="Call"
                          className="w-8 h-8 rounded-full border border-white/10 hover:border-[#D4AF37] hover:text-[#D4AF37] text-white/60 flex items-center justify-center transition-all"
                        >
                          <Phone className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </a>
                        <a
                          href={buildWhatsAppLink(
                            `Hello ${l.name}, this is from S Mahadeva Properties regarding your enquiry.`
                          )}
                          target="_blank"
                          rel="noreferrer"
                          title="WhatsApp"
                          className="w-8 h-8 rounded-full border border-white/10 hover:border-[#25D366] hover:text-[#25D366] text-white/60 flex items-center justify-center transition-all"
                        >
                          <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </a>
                        {l.email && (
                          <a
                            href={`mailto:${l.email}`}
                            title="Email"
                            className="w-8 h-8 rounded-full border border-white/10 hover:border-[#D4AF37] hover:text-[#D4AF37] text-white/60 flex items-center justify-center transition-all"
                          >
                            <Mail className="w-3.5 h-3.5" strokeWidth={1.5} />
                          </a>
                        )}
                        <button
                          onClick={() => remove(l.id)}
                          data-testid={`admin-delete-${l.id}`}
                          title="Delete"
                          className="w-8 h-8 rounded-full border border-white/10 hover:border-red-400 hover:text-red-400 text-white/60 flex items-center justify-center transition-all"
                        >
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return (
      d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }) +
      " · " +
      d.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
      })
    );
  } catch {
    return iso;
  }
}
